import * as THREE from 'three';
import { Utils } from '../utils';
import { debug } from '../potreeState';

import { Measure, MeasureTools, Point, MeasureConstructorArgs } from './Measure';
import type { EditableShape, EditableShapeKind, EditEdge } from '../personas/tools/implementations/line-tool/EditableShape';
import { SphereMarker } from './SphereMarker';
import type { ReactiveProperty, Subscription } from '@pointorama/potree-shared';
import {
  getAnnotationShowCoordinatesProperty,
  getAnnotationShowLengthsProperty,
  getAnnotationShowAreaProperty,
  getAnnotationShowVolumeProperty,
  getAnnotationShowHeightProperty,
  getPointPositionProperty,
  getDistancePointsProperty,
  getAreaPointsProperty,
  getAnnotationNameProperty,
  getOpeningKindProperty,
  getFloorPlanePositionProperty,
  hasFloorPlanePosition,
} from '@pointorama/potree-shared';
import type { AnnotationPoint } from '@pointorama/potree-shared';
import { OpeningKind } from 'shared-types';
import { openingNameForKind } from './openingKindNames';

export class PointoramaAnnotation extends Measure {
  lastMovedPoint: number | null = null;

  constructor(args: MeasureConstructorArgs) {
    super({ ...args });

    this.lineMaterial.linewidth = 2.0;
  }

  setPositionOfLastPoint(position: THREE.Vector3) {
    const i = this.points.length - 1;
    this.setPointData(i, position);
  }

  /** Subscribe to geometry property changes for live updates. Overridden by subclasses. */
  loadGeometryProperties(): void {
    // Base class no-op — subclasses override to subscribe to their specific properties.
  }

  canRemoveMarker() {
    return (this.type === MeasureTools.AREA && this.spheres.length > 3) || (this.type === MeasureTools.DISTANCE && this.spheres.length > 2);
  }

  getSphereIndex(sphere: SphereMarker) {
    const i = this.spheres.indexOf(sphere);
    return i;
  }

  addMarker(point: THREE.Vector3, index?: number) {
    if (index === undefined) {
      this.points.push({ position: point, pointCloudId: '' });
    } else {
      this.points.splice(index, 0, { position: point, pointCloudId: '' });
    }

    this.addSphere();

    this.addEdgeLabel();
    this.addCoordinateLabel();
  }

  addSphere() {
    const sphere = new SphereMarker(this.viewer);
    sphere.setColor(this.color);
    sphere.setScaleFactor(this.viewer.scene.measurementSphereScale);
    sphere.name = 'MeasureMarker';

    this.add(sphere);
    this.spheres.push(sphere);
  }

  setPointData(index: number, position: THREE.Vector3) {
    const point = this.points[index];
    point.position.copy(position);

    this.needsUpdate = true;
  }

  updatePositions() {
    if (this.points.length === 0) return;

    this.updateObjectPosition();

    if (this.points.length === 1) {
      this.updatePointPosition(0);
      this.updatePointLabelPosition(0);
      this.line.visible = false;
      if (this.backgroundLine) this.backgroundLine.visible = false;
      if (this.backgroundLineTouch) this.backgroundLineTouch.visible = false;
      return;
    }

    for (let index = 0; index < this.points.length; index++) {
      this.updatePointPosition(index);
      this.updatePointLabelPosition(index);

      this.updateEdgeLabelPosition(index);
    }

    this.updateLinePositions();

    this.updateAreaAndVolumeLabelPosition();
  }

  // Reusable temporaries to avoid per-frame allocations
  private static _tmpVec3 = new THREE.Vector3();
  private static _tmpVec3b = new THREE.Vector3();
  private static _tmpVec2 = new THREE.Vector2();
  private static _tmpMat4 = new THREE.Matrix4();

  /**
   * Recompute label scales + line resolutions to match a camera + canvas
   * size. With no args, reads the active main camera + main renderer size
   * (the original behavior). With args, used by per-RenderView render to
   * scale labels for the widget's camera/canvas before its render runs.
   */
  updateScales(cameraOverride?: THREE.PerspectiveCamera | THREE.OrthographicCamera, widthOverride?: number, heightOverride?: number) {
    const camera = cameraOverride ?? this.viewer.scene.getActiveCamera();

    if (this.lengthUnit !== this.viewer.lengthUnit || this.lengthUnitDisplay !== this.viewer.lengthUnitDisplay) {
      this.lengthUnit = this.viewer.lengthUnit;
      this.lengthUnitDisplay = this.viewer.lengthUnitDisplay;
      this.needsUpdate = true;
    }

    let clientWidth: number, clientHeight: number;
    if (widthOverride !== undefined && heightOverride !== undefined) {
      clientWidth = widthOverride;
      clientHeight = heightOverride;
    } else {
      const renderAreaSize = this.viewer.renderer.getSize(PointoramaAnnotation._tmpVec2);
      clientWidth = renderAreaSize.width;
      clientHeight = renderAreaSize.height;
    }

    const tmpVec = PointoramaAnnotation._tmpVec3;

    // labels
    const labels = this.edgeLabels;
    for (const label of labels) {
      const distance = camera.position.distanceTo(label.getWorldPosition(tmpVec));
      const pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
      let scale = 70 / pr;

      if (debug.scale) {
        scale = debug.scale / pr;
      }

      label.scale.set(scale, scale, scale);
    }

    // coordinate labels
    // Position coordinate labels above their associated spheres
    // Offset in screen-up direction so position is independent of camera angle
    const screenUp = tmpVec.set(0, 1, 0).applyQuaternion(camera.quaternion);
    const localScreenUp = screenUp.transformDirection(PointoramaAnnotation._tmpMat4.copy(this.matrixWorld).invert());

    for (let j = 0; j < this.coordinateLabels.length; j++) {
      const label = this.coordinateLabels[j];
      const sphere = this.spheres[j];

      const sphereWorldPos = sphere.getWorldPosition(PointoramaAnnotation._tmpVec3b);
      const distance = camera.position.distanceTo(sphereWorldPos);
      const pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
      const scale = 70 / pr;

      const labelOffset = scale * 0.5;
      label.position.copy(sphere.position);
      label.position.addScaledVector(localScreenUp, labelOffset);

      label.scale.set(scale, scale, scale);
    }

    // area label
    const areaLbl = this.areaLabel;
    if (areaLbl) {
      const distance = camera.position.distanceTo(areaLbl.getWorldPosition(tmpVec));
      const pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);

      const scale = 70 / pr;
      areaLbl.scale.set(scale, scale, scale);
    }

    // height label
    const heightLbl = this.heightLabel;
    if (heightLbl) {
      const distance = camera.position.distanceTo(heightLbl.getWorldPosition(tmpVec));
      const pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);

      const scale = 70 / pr;
      heightLbl.scale.set(scale, scale, scale);
    }

    // volume labels
    if (this.volumeLabels) {
      for (const volLabel of this.volumeLabels) {
        const distance = camera.position.distanceTo(volLabel.getWorldPosition(tmpVec));
        const pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);

        const scale = 70 / pr;
        volLabel.scale.set(scale, scale, scale);
      }
    }

    this.lineMaterial.resolution.set(clientWidth, clientHeight);
    this.backgroundLineMaterial.resolution.set(clientWidth, clientHeight);
    this.backgroundLineTouchMaterial.resolution.set(clientWidth, clientHeight);
  }

  /**
   * Replace all points with a new set, rebuilding spheres and labels.
   * Used by undo/redo to restore a previous point state.
   */
  setPoints(newPoints: Point[]): void {
    // Remove existing visual elements from the scene
    for (const sphere of this.spheres) this.remove(sphere);
    for (const label of this.edgeLabels) this.remove(label);
    for (const label of this.coordinateLabels) this.remove(label);

    this.points = [];
    this.spheres = [];
    this.edgeLabels = [];
    this.coordinateLabels = [];

    // Rebuild from new points (clone positions, preserve metadata)
    for (const p of newPoints) {
      this.points.push({
        position: p.position.clone(),
        pointCloudId: p.pointCloudId,
        color: p.color ? new Uint8Array(p.color) : undefined,
        classification: p.classification ? new Uint8Array(p.classification) : undefined,
        intensity: p.intensity ? new Float32Array(p.intensity) : undefined,
      });
      this.addSphere();
      this.addEdgeLabel();
      this.addCoordinateLabel();
    }

    this.needsUpdate = true;
  }

  getClosestPointOnLineSegment(pointIndex: number, point: THREE.Vector3): THREE.Vector3 {
    const segmentDirection = new THREE.Vector3().subVectors(this.points[pointIndex].position, this.points[(pointIndex + 1) % this.points.length].position).normalize();
    const v = new THREE.Vector3().subVectors(point, this.points[pointIndex].position);
    const distance = v.dot(segmentDirection);

    return this.points[pointIndex].position.clone().add(segmentDirection.multiplyScalar(distance));
  }
}

export class AreaAnnotation extends PointoramaAnnotation implements EditableShape {
  private _showLengthsSubscription?: Subscription;
  private _showAreaSubscription?: Subscription;
  private _showVolumeSubscription?: Subscription;
  private _pointsSubscription?: Subscription;

  // Edit mode snapshot — used by exitEditMode(false) to restore on cancel.
  private _editModeOriginalPoints: Point[] | null = null;
  private _isInEditMode = false;

  constructor({ points, ...args }: { points?: Point[] } & MeasureConstructorArgs) {
    super({ ...args });
    this.type = MeasureTools.AREA;
    this.domain = 'area';
    this.maxMarkers = Infinity;
    this.closed = true;
    if (points) points.forEach((point) => this.addMarker(point.position));
    if (this.finished) this.finishMeasurement();
    this.needsUpdate = true;
    this.loadLabelProperties();
  }

  /** Subscribe to geometry property changes for live updates */
  loadGeometryProperties(): void {
    if (!this.identifier) return;
    this._pointsSubscription?.unsubscribe();
    try {
      // Skip initial emission only if we already have points (from constructor).
      // If points are empty, accept the first value (optimistic cache may have no points).
      const hasPoints = this.points.length > 0;
      let init = false;
      const pointsProp = getAreaPointsProperty(this.identifier);
      this._pointsSubscription = pointsProp.subscribe((value: AnnotationPoint[]) => {
        if (hasPoints && !init) {
          init = true;
          return;
        }
        init = true;
        if (!value || !Array.isArray(value)) return;
        const newPoints = value.map((p) => ({
          position: new THREE.Vector3(p.position.x, p.position.y, p.position.z),
          pointCloudId: p.pointCloudId || undefined,
        }));
        this.setPoints(newPoints);
      });
    } catch {
      // Global property service not initialized yet
    }
  }

  private loadLabelProperties(): void {
    if (!this.identifier) return;
    try {
      const lengthsProp = getAnnotationShowLengthsProperty(this.identifier);
      this._showLengthsSubscription = lengthsProp.subscribe((value) => {
        this.showDistances = value;
      });

      const areaProp = getAnnotationShowAreaProperty(this.identifier);
      this._showAreaSubscription = areaProp.subscribe((value) => {
        this.showArea = value;
      });

      const volumeProp = getAnnotationShowVolumeProperty(this.identifier);
      this._showVolumeSubscription = volumeProp.subscribe((value) => {
        this.showVolume = value;
      });
    } catch {
      // Global property service not initialized yet
    }
  }

  // ─── EditableShape ──────────────────────────────────────────────────
  // Plain AREA annotations behave as `surface` shapes. Door/window
  // openings are now `OpeningAnnotation` (subclass below) and override
  // `getKind()` to return 'door_window'.

  getKind(): EditableShapeKind {
    return 'surface';
  }

  enterEditMode(): EditEdge[] {
    if (this._isInEditMode) return this.buildEditEdges();
    this._editModeOriginalPoints = this.points.map((p) => ({
      position: p.position.clone(),
      pointCloudId: p.pointCloudId,
      color: p.color ? new Uint8Array(p.color) : undefined,
      classification: p.classification ? new Uint8Array(p.classification) : undefined,
      intensity: p.intensity ? new Float32Array(p.intensity) : undefined,
    }));
    this._isInEditMode = true;

    // Hide visuals while the LineTool drives the edit. setPoints() at
    // exit will rebuild them.
    for (const sphere of this.spheres) sphere.visible = false;
    for (const label of this.edgeLabels) label.visible = false;
    for (const label of this.coordinateLabels) label.visible = false;
    if (this.line) this.line.visible = false;
    // Selection outline glow (backgroundLine) would otherwise stick around
    // while the LineTool draws its own edit visuals on top — looks doubled
    // up. exitEditMode's setPoints() → updateLinePositions() restores
    // visibility, and the `selected` flag stays set so the outline returns
    // with its prior opacity automatically.
    if (this.backgroundLine) this.backgroundLine.visible = false;

    return this.buildEditEdges();
  }

  exitEditMode(confirm: boolean, newPolygon?: THREE.Vector3[]): void {
    if (!this._isInEditMode) return;

    if (confirm && newPolygon && newPolygon.length >= 3) {
      const newPoints: Point[] = newPolygon.map((p) => ({ position: p.clone(), pointCloudId: '' }));
      this.setPoints(newPoints);
    } else if (!confirm && this._editModeOriginalPoints) {
      this.setPoints(this._editModeOriginalPoints);
    }

    // Make visuals visible again (setPoints rebuilds them in their
    // default-visible state, so this is a no-op when commit happened —
    // but covers the cancel-with-no-restore path too).
    for (const sphere of this.spheres) sphere.visible = true;
    for (const label of this.edgeLabels) label.visible = true;
    for (const label of this.coordinateLabels) label.visible = true;
    if (this.line) this.line.visible = true;

    this._isInEditMode = false;
    this._editModeOriginalPoints = null;
  }

  /**
   * Plane the rectangle sits on. For door/window AreaAnnotations this is
   * the wall plane; the LineTool reads `position.z` as the floor height
   * for some commits, but for door/window strips that's overridden by
   * the kind adapter's `configureViewForShape` (which sets the active
   * plane on `viewer.viewerMode`). Here we return the centroid.
   */
  getBasePlane(): { position: THREE.Vector3 } {
    if (this.points.length === 0) return { position: new THREE.Vector3() };
    const c = new THREE.Vector3();
    for (const p of this.points) c.add(p.position);
    c.divideScalar(this.points.length);
    return { position: c };
  }

  /** Build EditEdge[] from the polygon's points (closed loop). */
  private buildEditEdges(): EditEdge[] {
    const edges: EditEdge[] = [];
    const n = this.points.length;
    for (let i = 0; i < n; i++) {
      const start = this.points[i].position;
      const end = this.points[(i + 1) % n].position;
      const midpoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
      const direction = new THREE.Vector3().subVectors(end, start).normalize();
      edges.push({
        midpoint: midpoint.clone(),
        direction: direction.clone(),
        start: start.clone(),
        end: end.clone(),
      });
    }
    return edges;
  }

  override dispose(): void {
    this._pointsSubscription?.unsubscribe();
    this._showLengthsSubscription?.unsubscribe();
    this._showAreaSubscription?.unsubscribe();
    this._showVolumeSubscription?.unsubscribe();
    super.dispose();
  }
}

/**
 * Wall opening (door or window). Rendered as an AREA-shaped polygon — extends
 * AreaAnnotation so it inherits geometry, line/sphere rendering, and the AREA
 * branch in MeasureTools-driven logic. The subtype lives in the reactive
 * `opening_kind` property (DOOR | WINDOW); the optional link to a skeleton
 * edge lives in `opening_skeleton_id` + `opening_skeleton_edge_id`.
 */
export class OpeningAnnotation extends AreaAnnotation {
  private _kindProperty?: ReactiveProperty<OpeningKind>;
  private _kindSubscription?: Subscription;
  private _showHeightSubscription?: Subscription;
  private _floorPosSubscription?: Subscription;
  private _subscribedFloorId?: string;

  constructor(args: { points?: Point[] } & MeasureConstructorArgs) {
    super(args);
    this.domain = 'door_window';
    this.loadOpeningProperties();
  }

  private loadOpeningProperties(): void {
    if (!this.identifier) return;
    try {
      this._kindSubscription?.unsubscribe();
      this._kindProperty = getOpeningKindProperty(this.identifier);
      this.applyKindToName(this._kindProperty.value);
      this._kindSubscription = this._kindProperty.subscribeToChanges((kind) => {
        this.applyKindToName(kind);
        this.needsUpdate = true;
      });

      this._showHeightSubscription?.unsubscribe();
      const heightProp = getAnnotationShowHeightProperty(this.identifier);
      this._showHeightSubscription = heightProp.subscribe((value) => {
        this.showHeight = value;
      });
    } catch {
      // Global property service not initialized yet
    }
  }

  /** Update the annotation name to reflect the kind (Door / Window). */
  private applyKindToName(kind: OpeningKind): void {
    if (!this.identifier) return;
    try {
      const nameProperty = getAnnotationNameProperty(this.identifier);
      const next = openingNameForKind(nameProperty.value, kind, this.viewer.translationService);
      if (next !== nameProperty.value) nameProperty.value = next;
    } catch {
      // Global property service not initialized yet
    }
  }

  override getKind(): EditableShapeKind {
    return 'door_window';
  }

  /**
   * Windows show two stacked values: the window height (h) and the sill
   * elevation (↧, where the window starts) measured above the floor. Doors —
   * and windows without a defined floor — fall back to the plain height line.
   */
  protected override getHeightLabelText(): string {
    const heightValue = this.formatLength(this.getHeight());
    if (this._kindProperty?.value !== OpeningKind.WINDOW) return heightValue;

    const sill = this.getSillElevation();
    if (sill == null) return heightValue;

    // height on top, sill elevation below
    return `h ${heightValue}\n↧ ${this.formatLength(sill)}`;
  }

  /** Raw export values: window height, plus its sill elevation when defined. */
  override getHeightValues(): number[] {
    const height = this.getHeight();
    if (this._kindProperty?.value !== OpeningKind.WINDOW) return [height];
    const sill = this.getSillElevation();
    return sill == null ? [height] : [height, sill];
  }

  /**
   * Sill elevation = window bottom Z minus the floor-plane elevation of the
   * floor group this opening belongs to. Returns null when no floor exists, so
   * the caller omits the elevation line entirely.
   */
  private getSillElevation(): number | null {
    const floorId = this.groupIdentifier;
    if (!floorId || this.points.length === 0) return null;
    try {
      if (!hasFloorPlanePosition(floorId)) return null;
      const floorZ = getFloorPlanePositionProperty(floorId).value?.z;
      if (typeof floorZ !== 'number') return null;
      this.ensureFloorSubscription(floorId);
      const minZ = Math.min(...this.points.map((p) => p.position.z));
      return minZ - floorZ;
    } catch {
      return null;
    }
  }

  /** Keep the sill label live when the floor elevation is edited. */
  private ensureFloorSubscription(floorId: string): void {
    if (this._subscribedFloorId === floorId) return;
    this._floorPosSubscription?.unsubscribe();
    this._subscribedFloorId = floorId;
    try {
      this._floorPosSubscription = getFloorPlanePositionProperty(floorId).subscribeToChanges(() => {
        this.needsUpdate = true;
      });
    } catch {
      // Global property service not initialized yet
    }
  }

  override dispose(): void {
    this._kindSubscription?.unsubscribe();
    this._showHeightSubscription?.unsubscribe();
    this._floorPosSubscription?.unsubscribe();
    super.dispose();
  }
}

export class DistanceAnnotation extends PointoramaAnnotation {
  private _showLengthsSubscription?: Subscription;
  private _pointsSubscription?: Subscription;

  constructor({ points, ...args }: { points?: Point[] } & MeasureConstructorArgs) {
    super({ ...args });
    this.type = MeasureTools.DISTANCE;
    this.domain = 'distance';
    this.maxMarkers = Infinity;
    this.closed = false;
    if (points) points.forEach((point) => this.addMarker(point.position));
    if (this.finished) this.finishMeasurement();
    this.needsUpdate = true;
    this.loadLabelProperties();
  }

  /** Subscribe to geometry property changes for live updates */
  loadGeometryProperties(): void {
    if (!this.identifier) return;
    this._pointsSubscription?.unsubscribe();
    try {
      const hasPoints = this.points.length > 0;
      let init = false;
      const pointsProp = getDistancePointsProperty(this.identifier);
      this._pointsSubscription = pointsProp.subscribe((value: AnnotationPoint[]) => {
        if (hasPoints && !init) {
          init = true;
          return;
        }
        init = true;
        if (!value || !Array.isArray(value)) return;
        const newPoints = value.map((p) => ({
          position: new THREE.Vector3(p.position.x, p.position.y, p.position.z),
          pointCloudId: p.pointCloudId || undefined,
        }));
        this.setPoints(newPoints);
      });
    } catch {
      // Global property service not initialized yet
    }
  }

  private loadLabelProperties(): void {
    if (!this.identifier) return;
    try {
      const lengthsProp = getAnnotationShowLengthsProperty(this.identifier);
      this._showLengthsSubscription = lengthsProp.subscribe((value) => {
        this.showDistances = value;
      });
    } catch {
      // Global property service not initialized yet
    }
  }

  override dispose(): void {
    this._pointsSubscription?.unsubscribe();
    this._showLengthsSubscription?.unsubscribe();
    super.dispose();
  }
}

export class PointAnnotation extends PointoramaAnnotation {
  private _showCoordinatesSubscription?: Subscription;
  private _positionSubscription?: Subscription;

  constructor({ point, ...args }: { point?: Point } & MeasureConstructorArgs) {
    super({ ...args });
    this.type = MeasureTools.POINT;
    this.domain = 'point';
    this.maxMarkers = 1;
    this.closed = true;
    if (point) this.addMarker(point.position);
    if (this.finished) this.finishMeasurement();
    this.needsUpdate = true;
    this.loadLabelProperties();
  }

  /** Subscribe to geometry property changes for live updates */
  loadGeometryProperties(): void {
    if (!this.identifier) return;
    this._positionSubscription?.unsubscribe();
    try {
      const hasPoint = this.points.length > 0;
      let posInit = false;
      const posProp = getPointPositionProperty(this.identifier);
      this._positionSubscription = posProp.subscribe((value) => {
        if (hasPoint && !posInit) {
          posInit = true;
          return;
        }
        posInit = true;
        if (this.points[0]) {
          this.points[0].position.set(value.x, value.y, value.z);
        } else {
          this.addMarker(new THREE.Vector3(value.x, value.y, value.z));
        }
        this.needsUpdate = true;
      });
    } catch {
      // Global property service not initialized yet
    }
  }

  private loadLabelProperties(): void {
    if (!this.identifier) return;
    try {
      const coordProp = getAnnotationShowCoordinatesProperty(this.identifier);
      this._showCoordinatesSubscription = coordProp.subscribe((value) => {
        this.showCoordinates = value;
      });
    } catch {
      // Global property service not initialized yet
    }
  }

  override dispose(): void {
    this._positionSubscription?.unsubscribe();
    this._showCoordinatesSubscription?.unsubscribe();
    super.dispose();
  }
}

/**
 * True iff `m` is an `OpeningAnnotation` (door or window). Kept as an
 * exported helper so the LineTool kind-adapter machinery (and any other
 * caller that needs the narrowing) doesn't have to repeat the check.
 */
export function isDoorWindowAnnotation(m: Measure): m is OpeningAnnotation {
  return m instanceof OpeningAnnotation;
}
